import React, { MutableRefObject, ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import {
    DisplayOptions,
    LanguagePrompt,
    ReportIntervalLanguage,
    ReportOptions as ReportOptionsLanguage,
    ReportTypes as ReportTypesLanguage
} from '../../../../i18n'
import { REPORT_TYPE_GROUPS, ReportTypes } from '../../../NonReact/constants/ReportTypes'
// Utilities imports.
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import FormRow from '../../Common/Components/FormRow'
import Heading from '../../Common/Components/Heading'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { GroupedSelect, StandardOptionGroup } from '../../Common/Components/Selects/GroupedSelect/GroupedSelect'
import { SelectItem, SelectOptions } from '../../Common/Components/Selects/Types'
import TabActionPromptContext from '../../Common/Components/Tabs/TabActionPromptContext'
// Commons imports.
import TextInput from '../../Common/Components/TextInput'
import { ListReportDisplayedAsValues } from '../../Common/Constants/ReportDisplayedAsValues'
import { REPORT_STAT_FLAGS_TYPE_NONE } from '../../Common/Constants/ReportStatFlags'
// Contexts imports.
import { LanguageContext } from '../../Common/Contexts'
import AdvancedModeContext, { AdvancedModeContextType } from '../../Common/Contexts/AdvancedModeContext'
import ConfigureReportSettingsContext from '../../Common/Contexts/ConfigureReportSettingsContext'
import { ReactState, RptFilterEntityInfo } from '../../Common/Types/Types'
import { ReportDefinition, ReportDefs } from '../Types'
// Sub imports.
import { Select } from '../../Common/Components/Selects'
import {
    CallIntervalTypes,
    REPORT_CALL_BY_INTERVAL_ONE_HOUR_VALUE,
    REPORT_CALL_BY_INTERVAL_TWO_HOURS_VALUE,
    REPORT_CALLS_BY_INTERVAL_120_MINUTES_VALUE,
    REPORT_CALLS_BY_INTERVAL_12_HOUR_VALUE,
    REPORT_CALLS_BY_INTERVAL_15_MINUTES_VALUE,
    REPORT_CALLS_BY_INTERVAL_1_HOUR_VALUE,
    REPORT_CALLS_BY_INTERVAL_2_HOUR_VALUE,
    REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE,
    REPORT_CALLS_BY_INTERVAL_4_HOUR_VALUE,
    REPORT_CALLS_BY_INTERVAL_60_MINUTES_VALUE,
    REPORT_CALLS_BY_INTERVAL_8_HOUR_VALUE
} from './Constants'
import DateTimeSection from './DateTimeSection'
import ExternalDisplayOptions from './ExternalDisplayOptions'
import FolderField from './FolderField/FolderField'
import ListReportDisplayOptions from './ListReportDisplayOptions'
import WallboardDisplayOptions from './WallboardDisplayOptions'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'

// CSS imports. Commented out because of current build process.
// import './reportTab.scss'

/**
 * Component properties.
 */
type Props = {
    isPersonalDashboard: MutableRefObject<boolean>
    reportName: string
    setReportName: (reportName: string) => void
    note?: string
    setNote: (note: string) => void
    dateTimeOptionSelected: number
    setDateTimeOptionSelected: (value: number) => void
    reportDefinitions: ReportDefs | undefined
    selectedReportStyle: ReportDefinition | undefined
    updateSelectedReportStyle: (reportDefinition: ReportDefinition) => void
    displayedAsWallboardReport: number
    setDisplayedAsWallboardReport: (value: number) => void
    zoomLevel: string
    setZoomLevel: (value: string) => void
    isZoomLevelCustom: boolean
    setIsZoomLevelCustom: (value: boolean) => void
    chartType: number
    setChartType: (value: number) => void
    chartProvider: number
    setChartProvider: (value: number) => void
    externalURL: string
    setExternalURL: (value: string) => void
    displayedAsListReport: ListReportDisplayedAsValues
    setDisplayedAsListReport: (value: ListReportDisplayedAsValues) => void
    isShowingHeaderRowEnabled: boolean
    setIsShowingHeaderRowEnabled: (value: boolean) => void
    isShowingFooterRowEnabled: boolean
    setIsShowingFooterRowEnabled: (value: boolean) => void
    isShowingContentRowsEnabled: boolean
    setIsShowingContentRowsEnabled: (value: boolean) => void
    isShowingSpacerRowsEnabled: boolean
    setIsShowingSpacerRowsEnabled: (value: boolean) => void
    isShowingLargeBLFToolTipsEnabled: boolean
    setIsShowingLargeBLFToolTipsEnabled: (value: boolean) => void
    isAutomaticTextTruncationEnabled: boolean
    setIsAutomaticTextTruncationEnabled: (value: boolean) => void
    isWallboardReport: boolean
    isDashboardReport: boolean
    isExternalReport: boolean
    isTotalCallsByMinutesInterval: boolean
    isCallsByInterval: boolean
    isListReport: boolean
    folderPath: string
    setFolderPath: (value: string) => void
    filterEntityInfo: RptFilterEntityInfo | undefined
    reportTimeRangeStart: Date
    setReportTimeRangeStart: (value: Date) => void
    reportTimeRangeEnd: Date
    setReportTimeRangeEnd: (value: Date) => void
    reportDateTimeRangeStart: Date
    setReportDateTimeRangeStart: (value: Date) => void
    reportDateTimeRangeEnd: Date
    setReportDateTimeRangeEnd: (value: Date) => void
    reportDateTimeDaysOfTheWeek: string[]
    setReportDateTimeDaysOfTheWeek: (value: string[]) => void
    reportDateTimeLastXDays: number | string
    setReportDateTimeLastXDays: (value: number | string) => void
    totalCallByInterval: number
    setTotalCallByInterval: (value: number) => void
    callByIntervalType: number
    setCallByIntervalType: (value: number) => void
    isCurrentTelSysHistoric: boolean
}

/**
 * Report tab component.
 *
 * @param reportName
 * @param setReportName
 * @param note
 * @param setNote
 * @param dateTimeOptionSelected
 * @param setDateTimeOptionSelected
 * @param selectedReportStyle Currently selected report style - report definition object representing report style.
 * @param updateSelectedReportStyle Update method for report style property that also handles required side effects appropriately.
 * @param displayedAsWallboardReport Selected display option for wallboard report.
 * @param setDisplayedAsWallboardReport Method that sets the selected display type.
 * @param zoomLevel Zoom level percentage value.
 * @param setZoomLevel Method that sets the zoom level value.
 * @param isZoomLevelCustom Zoom level auto checkbox.
 * @param setIsZoomLevelCustom Method that sets the zoom auto checkbox.
 * @param chartType Selected chart style option for list reports.
 * @param setChartType Method that sets the selected chart style.
 * @param chartProvider Selected chart provider option for list reports.
 * @param setChartProvider Method that sets the selected chart provider.
 * @param externalURL External URL string.
 * @param setExternalURL Sets the external URL string.
 * @param displayedAsListReport Selected display option for list reports.
 * @param setDisplayedAsListReport Method that sets the selected display type.
 * @param setIsShowingHeaderRowEnabled Setter method of the show header checkbox.
 * @param isShowingFooterRowEnabled Boolean defining the state of the show footer checkbox.
 * @param setIsShowingFooterRowEnabled Setter method of the show footer checkbox.
 * @param isShowingContentRowsEnabled Boolean defining the state of the show content checkbox.
 * @param setIsShowingContentRowsEnabled Setter method of the show content checkbox.
 * @param isShowingSpacerRowsEnabled Boolean defining the state of the show spacer checkbox.
 * @param setIsShowingSpacerRowsEnabled Setter method of the show spacer checkbox.
 * @param isShowingLargeBLFToolTipsEnabled Boolean defining the state of the show BLF tooltips checkbox.
 * @param setIsShowingLargeBLFToolTipsEnabled Setter method of the show BLF tooltips checkbox.
 * @param isAutomaticTextTruncationEnabled Boolean defining the state of the automatic text truncation checkbox.
 * @param setIsAutomaticTextTruncationEnabled Setter method of the automatic text truncation checkbox.
 * @param isWallboardReport Boolean defining wallboard report type.
 * @param isDashboardReport Boolean defining dashboard report type.
 * @param isExternalReport Boolean defining external report type.
 * @param isTotalCallsByMinutesInterval Boolean defining total calls by minutes interval report type.
 * @param isCallsByInterval Boolean defining calls by interval report type.
 * @param isListReport Boolean defining list report type.
 * @param folderPath String value defining the folder path.
 * @param setFolderPath Setter method of the folder path input field.
 * @param filterEntityInfo The list of entities according to the filter with capability flags.
 * @param reportTimeRangeStart Start of report time range.
 * @param setReportTimeRangeStart Setter for property.
 * @param reportTimeRangeEnd End of report time range.
 * @param setReportTimeRangeEnd Setter for the property.
 * @param reportDateTimeRangeStart Start of report date/time range.
 * @param setReportDateTimeRangeStart Setter for the property.
 * @param reportDateTimeRangeEnd End of report date/time range.
 * @param setReportDateTimeRangeEnd Setter for the property.
 * @param reportDateTimeDaysOfTheWeek Days of the week for report duration.
 * @param reportDateTimeDaysOfTheWeek Setter for the property.
 * @param totalCallByInterval Total calls by interval.
 * @param setTotalCallByInterval Setter for the property.
 */
const ReportTab: React.FC<Props> = ({
    isPersonalDashboard,
    reportName,
    setReportName,
    note,
    setNote,
    dateTimeOptionSelected,
    setDateTimeOptionSelected,
    reportDefinitions,
    selectedReportStyle,
    updateSelectedReportStyle,
    displayedAsWallboardReport,
    setDisplayedAsWallboardReport,
    zoomLevel,
    setZoomLevel,
    isZoomLevelCustom,
    setIsZoomLevelCustom,
    chartType,
    setChartType,
    chartProvider,
    setChartProvider,
    externalURL,
    setExternalURL,
    displayedAsListReport,
    setDisplayedAsListReport,
    isShowingHeaderRowEnabled,
    setIsShowingHeaderRowEnabled,
    isShowingFooterRowEnabled,
    setIsShowingFooterRowEnabled,
    isShowingContentRowsEnabled,
    setIsShowingContentRowsEnabled,
    isShowingSpacerRowsEnabled,
    setIsShowingSpacerRowsEnabled,
    isShowingLargeBLFToolTipsEnabled,
    setIsShowingLargeBLFToolTipsEnabled,
    isAutomaticTextTruncationEnabled,
    setIsAutomaticTextTruncationEnabled,
    isWallboardReport,
    isDashboardReport,
    isExternalReport,
    isTotalCallsByMinutesInterval,
    isCallsByInterval,
    isListReport,
    folderPath,
    setFolderPath,
    filterEntityInfo,
    reportTimeRangeStart,
    setReportTimeRangeStart,
    reportTimeRangeEnd,
    setReportTimeRangeEnd,
    reportDateTimeRangeStart,
    setReportDateTimeRangeStart,
    reportDateTimeRangeEnd,
    setReportDateTimeRangeEnd,
    reportDateTimeDaysOfTheWeek,
    setReportDateTimeDaysOfTheWeek,
    reportDateTimeLastXDays,
    setReportDateTimeLastXDays,
    totalCallByInterval,
    setTotalCallByInterval,
    callByIntervalType,
    setCallByIntervalType,
    isCurrentTelSysHistoric
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    const { displayPrompt } = useContext(TabActionPromptContext)
    const { isDateTimeOnly } = useContext(ConfigureReportSettingsContext)
    const { isAdvancedMode }: AdvancedModeContextType = useContext(AdvancedModeContext)

    const [updatingFirstTime, setUpdatingFirstTime]: ReactState<boolean> = useState<boolean>(true)

    language.loadBundle(ReportOptionsLanguage.NAME)
    language.loadBundle(ReportTypesLanguage.NAME)
    language.loadBundle(ReportIntervalLanguage.NAME)

    const DEFAULT_REPORT_TYPE_GROUPS: StandardOptionGroup[] = [
        {
            options: []
        },
        {
            groupTitle: language.get(ReportTypesLanguage.GroupLive),
            options: []
        },
        {
            groupTitle: language.get(ReportTypesLanguage.GroupEntity),
            options: []
        },
        {
            groupTitle: language.get(ReportTypesLanguage.GroupLogs),
            options: []
        },
        {
            groupTitle: language.get(ReportTypesLanguage.GroupInterval),
            options: []
        },
        {
            groupTitle: language.get(ReportTypesLanguage.GroupMisc),
            options: []
        }
    ]

    const intervalOptions: SelectOptions<number> = [
        {
            value: REPORT_CALLS_BY_INTERVAL_15_MINUTES_VALUE,
            label: language.get(ReportIntervalLanguage.ReportIntervalLanguageFifteenMinutes)
        },
        {
            value: REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE,
            label: language.get(ReportIntervalLanguage.ReportIntervalLanguageThirtyMinutes)
        },
        {
            value: REPORT_CALLS_BY_INTERVAL_60_MINUTES_VALUE,
            label: language.get(ReportIntervalLanguage.ReportIntervalLanguageOneHour)
        },
        {
            value: REPORT_CALLS_BY_INTERVAL_120_MINUTES_VALUE,
            label: language.get(ReportIntervalLanguage.ReportIntervalLanguageTwoHour)
        }
    ]

    const intervalMinuteOptions: SelectOptions<number> = [
        {
            value: REPORT_CALLS_BY_INTERVAL_15_MINUTES_VALUE,
            label: `${REPORT_CALLS_BY_INTERVAL_15_MINUTES_VALUE}`
        },
        {
            value: REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE,
            label: `${REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE}`
        }
    ]

    const intervalHoursOptions: SelectOptions<number> = [
        {
            value: REPORT_CALLS_BY_INTERVAL_1_HOUR_VALUE,
            label: `${REPORT_CALLS_BY_INTERVAL_1_HOUR_VALUE}`
        },
        {
            value: REPORT_CALLS_BY_INTERVAL_2_HOUR_VALUE,
            label: `${REPORT_CALLS_BY_INTERVAL_2_HOUR_VALUE}`
        },
        {
            value: REPORT_CALLS_BY_INTERVAL_4_HOUR_VALUE,
            label: `${REPORT_CALLS_BY_INTERVAL_4_HOUR_VALUE}`
        },
        {
            value: REPORT_CALLS_BY_INTERVAL_8_HOUR_VALUE,
            label: `${REPORT_CALLS_BY_INTERVAL_8_HOUR_VALUE}`
        },
        {
            value: REPORT_CALLS_BY_INTERVAL_12_HOUR_VALUE,
            label: `${REPORT_CALLS_BY_INTERVAL_12_HOUR_VALUE}`
        }
    ]

    const intervalTypesOptions: SelectOptions<number> = [
        {
            value: CallIntervalTypes.MINUTES,
            label: language.get(ReportIntervalLanguage.ReportIntervalTypeMinutes)
        },
        {
            value: CallIntervalTypes.HOURS,
            label: language.get(ReportIntervalLanguage.ReportIntervalTypeHours)
        },
        {
            value: CallIntervalTypes.DAYS,
            label: language.get(ReportIntervalLanguage.ReportIntervalTypeDay)
        },
        {
            value: CallIntervalTypes.WEEK,
            label: language.get(ReportIntervalLanguage.ReportIntervalTypeWeek)
        },
        {
            value: CallIntervalTypes.MONTH,
            label: language.get(ReportIntervalLanguage.ReportIntervalTypeMonth)
        }
    ]

    const mapIntervalCallTypeIntToString = (type: number): string => {
        switch (type) {
            case CallIntervalTypes.MINUTES:
                return language.get(ReportIntervalLanguage.ReportIntervalTypeMinutes)
            case CallIntervalTypes.HOURS:
                return language.get(ReportIntervalLanguage.ReportIntervalTypeHours)
            case CallIntervalTypes.DAYS:
                return language.get(ReportIntervalLanguage.ReportIntervalTypeDay)
            case CallIntervalTypes.WEEK:
                return language.get(ReportIntervalLanguage.ReportIntervalTypeWeek)
            case CallIntervalTypes.MONTH:
                return language.get(ReportIntervalLanguage.ReportIntervalTypeMonth)
            default:
                return language.get(ReportIntervalLanguage.ReportIntervalTypeMinutes)
        }
    }

    const getGroupedReportStyles = (): StandardOptionGroup[] => {
        // Maps report definitions and retrieves list of report style options and their correct names based on the
        // current user flags.
        const statFlags: Nullable<string> = filterEntityInfo?.EntityInfo.ReportCapabilityInfo.StatFlags
        const styleOptions: Nullable<(SelectItem & { reportType: number })[]> = reportDefinitions?.map(
            (reportDef: ReportDefinition) => {
                const isLicensedForRptType = (Number(statFlags) & reportDef.StatFlagsType) > REPORT_STAT_FLAGS_TYPE_NONE

                const reportLabel = reportDef.DescDefaultACDOrLower
                const unlicensedFormattingString = language.get(
                    ReportTypesLanguage.ReporttypeUnlicensedformattingstring
                )

                return {
                    reportType: reportDef.ReportType,
                    value: reportDef.ReportType.toString(),
                    label: isLicensedForRptType
                        ? reportLabel
                        : StringExpandPlaceholders(unlicensedFormattingString, [reportLabel])
                }
            }
        )

        return (
            styleOptions?.reduce(
                (
                    accum: StandardOptionGroup[],
                    { reportType, ...selectItem }: SelectItem & { reportType: number }
                ): StandardOptionGroup[] => {
                    const groupIndex: number = REPORT_TYPE_GROUPS.findIndex((group: number[]): boolean =>
                        group.includes(reportType)
                    )

                    if (groupIndex > -1) {
                        accum[groupIndex].options.push(selectItem)
                    }

                    return accum
                },
                DEFAULT_REPORT_TYPE_GROUPS
            ) ?? []
        )
    }

    const intervalValueMap: Record<number, string> = {
        [REPORT_CALLS_BY_INTERVAL_15_MINUTES_VALUE]: `${REPORT_CALLS_BY_INTERVAL_15_MINUTES_VALUE}`,
        [REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE]: `${REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE}`,
        [REPORT_CALLS_BY_INTERVAL_60_MINUTES_VALUE]: REPORT_CALL_BY_INTERVAL_ONE_HOUR_VALUE,
        [REPORT_CALLS_BY_INTERVAL_120_MINUTES_VALUE]: REPORT_CALL_BY_INTERVAL_TWO_HOURS_VALUE
    }

    const intervalValueToTypeMap: Record<number, number> = {
        [REPORT_CALLS_BY_INTERVAL_15_MINUTES_VALUE]: CallIntervalTypes.MINUTES,
        [REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE]: CallIntervalTypes.MINUTES,
        [REPORT_CALLS_BY_INTERVAL_60_MINUTES_VALUE]: CallIntervalTypes.HOURS,
        [REPORT_CALLS_BY_INTERVAL_120_MINUTES_VALUE]: CallIntervalTypes.HOURS
    }

    const intervalTypeMap: Record<number, string> = {
        [REPORT_CALLS_BY_INTERVAL_15_MINUTES_VALUE]: language.get(
            ReportIntervalLanguage.ReportIntervalLanguageTypeMinute
        ),
        [REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE]: language.get(
            ReportIntervalLanguage.ReportIntervalLanguageTypeMinute
        ),
        [REPORT_CALLS_BY_INTERVAL_60_MINUTES_VALUE]: language.get(
            ReportIntervalLanguage.ReportIntervalLanguageTypeHour
        ),
        [REPORT_CALLS_BY_INTERVAL_120_MINUTES_VALUE]: language.get(
            ReportIntervalLanguage.ReportIntervalLanguageTypeHourPluralised
        )
    }

    const setDefaultValuesForIntervalValueType = (): void => {
        setTotalCallByInterval(REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE)
        setCallByIntervalType(CallIntervalTypes.MINUTES)
    }

    const handleReportDefinitionChange = (value: string): void => {
        const newlySelectedReportDefinition = reportDefinitions?.find(
            (reportDef: ReportDefinition) => Number(value) === reportDef.ReportType
        )

        if (!!newlySelectedReportDefinition && !!selectedReportStyle) {
            updateSelectedReportStyle(newlySelectedReportDefinition)

            if (reportName !== selectedReportStyle.DescDefaultACDOrLower) {
                // If user edited default report name, we display prompt whether user wants to replace it or not.
                const prompMessageFormatString = language?.get(
                    ReportOptionsLanguage.ReporttabReportnameoverwritePromptTextFormatString
                )

                const newReportName = newlySelectedReportDefinition.DescDefaultACDOrLower
                const promptMessage = StringExpandPlaceholders(prompMessageFormatString, [newReportName])
                // Display prompt whether to replace Report name - if user clicks cancel, we leave the report name as is.
                displayPrompt(
                    promptMessage,
                    `${language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm)}`,
                    `${language?.get(LanguagePrompt.LanguagePrompt_PromptCancel)}`,
                    () => {
                        if (Number(value) === ReportTypes.TOTAL_CALL_BY_INTERVAL) {
                            setDefaultValuesForIntervalValueType()
                            setReportName(
                                language.getInterpolated(
                                    ReportIntervalLanguage.ReportIntervalLanguageInterpolated,
                                    REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE,
                                    intervalTypeMap[REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE]
                                )
                            )
                        } else if (Number(value) === ReportTypes.CALL_BY_INTERVAL) {
                            setDefaultValuesForIntervalValueType()
                            setReportName(
                                language.getInterpolated(
                                    ReportIntervalLanguage.ReportIntervalLanguageCallByInterpolated,
                                    REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE,
                                    mapIntervalCallTypeIntToString(CallIntervalTypes.MINUTES)
                                )
                            )
                        } else {
                            setReportName(newReportName)
                        }
                    }
                )
            } else if (Number(value) === ReportTypes.TOTAL_CALL_BY_INTERVAL) {
                setDefaultValuesForIntervalValueType()
                setReportName(
                    language.getInterpolated(
                        ReportIntervalLanguage.ReportIntervalLanguageInterpolated,
                        REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE,
                        intervalTypeMap[REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE]
                    )
                )
            } else if (Number(value) === ReportTypes.CALL_BY_INTERVAL) {
                setDefaultValuesForIntervalValueType()
                setReportName(
                    language.getInterpolated(
                        ReportIntervalLanguage.ReportIntervalLanguageCallByInterpolated,
                        REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE,
                        mapIntervalCallTypeIntToString(CallIntervalTypes.MINUTES)
                    )
                )
            } else {
                // IF the default name was not edited, we directly replace the report name without prompt.
                setReportName(newlySelectedReportDefinition.DescDefaultACDOrLower)
            }
        }
    }

    const [isOpen, setIsOpen] = useState(false)

    const handleShowPopper = useCallback((): void => {
        setIsOpen(!isOpen)
    }, [isOpen])

    const getIntervalValueOptions = (): SelectOptions<number> => {
        if (isTotalCallsByMinutesInterval) return intervalOptions
        if (callByIntervalType === CallIntervalTypes.MINUTES) return intervalMinuteOptions
        if (callByIntervalType === CallIntervalTypes.HOURS) return intervalHoursOptions

        return intervalOptions
    }

    useEffect((): void => {
        // prevents the report name from being updated when loading existing report
        if (updatingFirstTime) {
            setUpdatingFirstTime(false)
            return
        }

        if (isTotalCallsByMinutesInterval) {
            setReportName(
                language.getInterpolated(
                    ReportIntervalLanguage.ReportIntervalLanguageInterpolated,
                    intervalValueMap[totalCallByInterval],
                    intervalTypeMap[totalCallByInterval]
                )
            )
        } else if (isCallsByInterval) {
            if (callByIntervalType === CallIntervalTypes.MINUTES || callByIntervalType === CallIntervalTypes.HOURS) {
                setReportName(
                    language.getInterpolated(
                        ReportIntervalLanguage.ReportIntervalLanguageCallByInterpolated,
                        totalCallByInterval,
                        mapIntervalCallTypeIntToString(callByIntervalType)
                    )
                )
            } else {
                setReportName(
                    language.getInterpolated(
                        ReportIntervalLanguage.ReportIntervalLanguageCallByNoValueInterpolated,
                        mapIntervalCallTypeIntToString(callByIntervalType)
                    )
                )
            }
        }
    }, [callByIntervalType, totalCallByInterval])

    return (
        <div className='report-tab tab-area'>
            {/* Reports options section. */}
            {!isDateTimeOnly && (
                <>
                    <Heading level={HeadingLevel.ONE}>
                        {language?.get(ReportOptionsLanguage.SectionheaderReportoptions)}
                    </Heading>
                    {selectedReportStyle && (
                        <div className='report-options'>
                            <FormRow className={'report-style'}>
                                <GroupedSelect
                                    labelInputClassName='report-style'
                                    label={language?.get(ReportOptionsLanguage.ReporttablabelStyle)}
                                    value={String(selectedReportStyle.ReportType)}
                                    handleChange={(newValue?: string | string[]): void => {
                                        if (newValue && !Array.isArray(newValue)) {
                                            handleReportDefinitionChange(newValue)
                                        } else if (Array.isArray(newValue) && newValue.length > 0) {
                                            handleReportDefinitionChange(newValue[0])
                                        }
                                    }}
                                    options={getGroupedReportStyles()}
                                />
                            </FormRow>
                            {isTotalCallsByMinutesInterval || isCallsByInterval ? (
                                <FormRow
                                    className={`${
                                        isCallsByInterval &&
                                        (callByIntervalType === CallIntervalTypes.MINUTES ||
                                            callByIntervalType === CallIntervalTypes.HOURS)
                                            ? 'interval-formrow'
                                            : undefined
                                    }`}
                                    isInline
                                >
                                    {isCallsByInterval ? (
                                        <Select
                                            classNames='interval-type-select'
                                            label={language.get(ReportOptionsLanguage.ReporttablabelInterval)}
                                            value={callByIntervalType}
                                            handleChange={(newValue: number): void => {
                                                setCallByIntervalType(newValue)
                                                if (newValue === CallIntervalTypes.MINUTES) {
                                                    setTotalCallByInterval(REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE)
                                                } else if (newValue === CallIntervalTypes.HOURS) {
                                                    setTotalCallByInterval(REPORT_CALLS_BY_INTERVAL_1_HOUR_VALUE)
                                                } else {
                                                    setTotalCallByInterval(REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE)
                                                }
                                            }}
                                            options={intervalTypesOptions}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                    {!isCallsByInterval ||
                                    (isCallsByInterval &&
                                        (callByIntervalType === CallIntervalTypes.MINUTES ||
                                            callByIntervalType === CallIntervalTypes.HOURS)) ? (
                                        <Select
                                            classNames='interval-value-select'
                                            label={
                                                isCallsByInterval
                                                    ? EMPTY_STRING
                                                    : language.get(ReportOptionsLanguage.ReporttablabelInterval)
                                            }
                                            value={totalCallByInterval}
                                            handleChange={(newValue: number): void => {
                                                setTotalCallByInterval(newValue)

                                                if (isTotalCallsByMinutesInterval) {
                                                    setCallByIntervalType(intervalValueToTypeMap[newValue])
                                                }
                                            }}
                                            options={getIntervalValueOptions()}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                </FormRow>
                            ) : (
                                <></>
                            )}
                            <TextInput
                                className={'report-name'}
                                labelInputClassName='report-name-containers'
                                value={reportName}
                                labelLeft={language?.get(ReportOptionsLanguage.ReporttablabelName)}
                                onChange={setReportName}
                            />
                            <FolderField
                                classNames='report-folder'
                                folderPath={folderPath}
                                setFolderPath={setFolderPath}
                                autoCompleteCallback={handleShowPopper}
                                isOpen={isOpen}
                                setIsOpen={setIsOpen}
                            />
                            {isAdvancedMode ? (
                                <TextInput
                                    className={'report-label'}
                                    value={note}
                                    labelLeft={language?.get(ReportOptionsLanguage.ReporttablabelNote)}
                                    onChange={(value): void => {
                                        setNote(value)
                                    }}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Display options section. */}
            {!isDashboardReport && (
                <React.Fragment>
                    {!isDateTimeOnly && isAdvancedMode && (
                        <>
                            <Heading level={HeadingLevel.ONE}>{language?.get(DisplayOptions.DisplayOptions)}</Heading>
                            <div className='display-options'>
                                {isWallboardReport && (
                                    <WallboardDisplayOptions
                                        isPersonalDashboard={isPersonalDashboard}
                                        displayedAsWallboardReport={displayedAsWallboardReport}
                                        setDisplayedAsWallboardReport={setDisplayedAsWallboardReport}
                                        chartType={chartType}
                                        setChartType={setChartType}
                                        chartProvider={chartProvider}
                                        setChartProvider={setChartProvider}
                                    />
                                )}
                                {isExternalReport && (
                                    <div className='external-content'>
                                        <ExternalDisplayOptions
                                            externalURL={externalURL}
                                            setExternalURL={setExternalURL}
                                        />
                                    </div>
                                )}
                                {isListReport && (
                                    <ListReportDisplayOptions
                                        isPersonalDashboard={isPersonalDashboard}
                                        displayedAsListReport={displayedAsListReport}
                                        setDisplayedAsListReport={setDisplayedAsListReport}
                                        zoomLevel={zoomLevel}
                                        setZoomLevel={setZoomLevel}
                                        isZoomLevelCustom={isZoomLevelCustom}
                                        setIsZoomLevelCustom={setIsZoomLevelCustom}
                                        chartType={chartType}
                                        setChartType={setChartType}
                                        chartProvider={chartProvider}
                                        setChartProvider={setChartProvider}
                                        isShowingHeaderRowEnabled={isShowingHeaderRowEnabled}
                                        setIsShowingHeaderRowEnabled={setIsShowingHeaderRowEnabled}
                                        isShowingFooterRowEnabled={isShowingFooterRowEnabled}
                                        setIsShowingFooterRowEnabled={setIsShowingFooterRowEnabled}
                                        isShowingContentRowsEnabled={isShowingContentRowsEnabled}
                                        setIsShowingContentRowsEnabled={setIsShowingContentRowsEnabled}
                                        isShowingSpacerRowsEnabled={isShowingSpacerRowsEnabled}
                                        setIsShowingSpacerRowsEnabled={setIsShowingSpacerRowsEnabled}
                                        isShowingLargeBLFToolTipsEnabled={isShowingLargeBLFToolTipsEnabled}
                                        setIsShowingLargeBLFToolTipsEnabled={setIsShowingLargeBLFToolTipsEnabled}
                                        isAutomaticTextTruncationEnabled={isAutomaticTextTruncationEnabled}
                                        setIsAutomaticTextTruncationEnabled={setIsAutomaticTextTruncationEnabled}
                                    />
                                )}
                            </div>
                        </>
                    )}

                    {/* Date / time options section. */}
                    <DateTimeSection
                        isPersonalDashboard={isPersonalDashboard}
                        dateTimeOptionSelected={dateTimeOptionSelected}
                        setDateTimeOptionSelected={setDateTimeOptionSelected}
                        selectedReportStyle={selectedReportStyle}
                        reportTimeRangeStart={reportTimeRangeStart}
                        setReportTimeRangeStart={setReportTimeRangeStart}
                        reportTimeRangeEnd={reportTimeRangeEnd}
                        setReportTimeRangeEnd={setReportTimeRangeEnd}
                        reportDateTimeRangeStart={reportDateTimeRangeStart}
                        setReportDateTimeRangeStart={setReportDateTimeRangeStart}
                        reportDateTimeRangeEnd={reportDateTimeRangeEnd}
                        setReportDateTimeRangeEnd={setReportDateTimeRangeEnd}
                        reportDateTimeDaysOfTheWeek={reportDateTimeDaysOfTheWeek}
                        setReportDateTimeDaysOfTheWeek={setReportDateTimeDaysOfTheWeek}
                        reportDateTimeLastXDays={reportDateTimeLastXDays}
                        setReportDateTimeLastXDays={setReportDateTimeLastXDays}
                        isCurrentTelSysHistoric={isCurrentTelSysHistoric}
                    />
                </React.Fragment>
            )}
        </div>
    )
}

export default ReportTab
